import { useState, useMemo, useEffect, useCallback } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LabelIcon from '@mui/icons-material/Label';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useGetAuthApi, usePostAuthApiQuery, usePostAuthApi, usePutAuthApi } from '../../../hooks/UseApiCalls';
import { ManagementInquiryDtoIn, PaginatedInquiriesResult } from '../../../models/in/ManagementInquiryDtoIn';
import { InquiryDetailDtoIn, InquiryMessageDtoIn } from '../../../models/in/InquiryDetailDtoIn';
import { InquiriesQueryDtoOut } from '../../../models/out/InquiriesQueryDtoOut';
import { InquiryStatsDtoIn } from '../../../models/in/InquiryStatsDtoIn';
import { InquiryStatus } from '../../../models/common/InquiryEnums';
import InquiryStatusChip from '../../../components/Inquiries/InquiryStatusChip';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import LabelChip from '../../../components/Inquiries/LabelChip';
import ChatPanel from '../../../components/Inquiries/ChatPanel';
import InquiryResourceSummary from '../../../components/Inquiries/InquiryResourceSummary';
import InquiryAlertSettingsButton from '../../../components/Inquiries/InquiryAlertSettingsButton';
import LabelPicker from '../../../components/Inquiries/LabelPicker';
import { LabelDtoIn } from '../../../models/in/LabelDtoIn';
import { CreateLabelRequestBody } from '../../../models/out/CreateLabelRequestBody';
import AssigneePicker from '../../../components/Inquiries/AssigneePicker';
import DataTable from '../../../components/ui/DataTable/DataTable';
import Blade from '../../../components/ui/Blade/Blade';
import Button from '../../../components/ui/Button';
import useTableDensity from '../../../hooks/useTableDensity';
import CustomDialog from '../../../components/ui/Dialog/Dialog';
import { useQueryClient } from '@tanstack/react-query';
import useQueryRefreshInterval from '../../../hooks/useQueryRefreshInterval';
import styles from './InquiriesManagementPage.module.scss';

const statusTabs = ['All', 'New', 'WaitingForOffice', 'WaitingForUser', 'AnswerReceived', 'Closed'] as const;
const DENSITY_STORAGE_KEY = 'table.density.inquiriesManagement';

const tabKeyMap: Record<string, string> = {
  All: 'all',
  New: 'new',
  WaitingForOffice: 'waitingForOffice',
  WaitingForUser: 'waitingForUser',
  AnswerReceived: 'answerReceived',
  Closed: 'closed',
};

export default function InquiriesManagementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { inquiryId } = useParams();
  const queryClient = useQueryClient();

  const [statusTab, setStatusTab] = useState<string>('All');
  const [scope, setScope] = useState<string>('All');
  const [search, setSearch] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const [page, setPage] = useState(1);
  const [labelAnchor, setLabelAnchor] = useState<HTMLElement | null>(null);
  const [assigneeAnchor, setAssigneeAnchor] = useState<HTMLElement | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [filterLabelIds, setFilterLabelIds] = useState<number[]>([]);
  const [labelFilterAnchor, setLabelFilterAnchor] = useState<HTMLElement | null>(null);
  const [showCreateLabel, setShowCreateLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');

  useEffect(() => {
    setPage(1);
  }, [search]);

  const query: InquiriesQueryDtoOut = {
    statusTab, scope, fundIds: [], departmentIds: [], assignedToEmails: [],
    labelIds: filterLabelIds, search, includeHidden: false, page, pageSize: 50,
  };

  const { data: allLabels } = useGetAuthApi<LabelDtoIn[]>('management/labels');
  const createLabelMutation = usePostAuthApi<LabelDtoIn, CreateLabelRequestBody>('management/labels', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['management/labels'] });
      setNewLabelName('');
      setShowCreateLabel(false);
    },
  });
  const { data: stats } = useGetAuthApi<InquiryStatsDtoIn>(`management/inquiries/stats?scope=${scope}`);
  const { data: result, isLoading } = usePostAuthApiQuery<PaginatedInquiriesResult>('management/inquiries/query', query);

  const parsedId = inquiryId ? parseInt(inquiryId) : NaN;
  const selectedId = Number.isFinite(parsedId) ? parsedId : null;
  const detailUrl = selectedId ? `management/inquiries/${selectedId}` : '';
  const { data: detail } = useGetAuthApi<InquiryDetailDtoIn>(detailUrl);

  /*
   * The coordinator answers while a secretary has the thread open, and nothing here noticed:
   * the thread and the table were fetched once and stood still until a manual reload.
   */
  useQueryRefreshInterval([detailUrl, 'management/inquiries/query'], 15000);

  const messageUrl = selectedId ? `management/inquiries/${selectedId}/messages` : '';
  const sendMessageMutation = usePostAuthApi(messageUrl, {
    onSuccess: () => { if (detailUrl) queryClient.invalidateQueries({ queryKey: [detailUrl] }); },
    onError: () => { if (detailUrl) queryClient.invalidateQueries({ queryKey: [detailUrl] }); },
  });

  const sendMessage = (content: string, isInternalNote: boolean, replyToId: number | null, documentIds: number[]) => {
    if (!selectedId) return;

    const optimisticMsg: InquiryMessageDtoIn = {
      id: Date.now(),
      senderName: '',
      senderRole: 'Office',
      content,
      replyToMessageId: replyToId,
      replyToPreview: null,
      isSystemMessage: false,
      isInternalNote,
      mentionedUserEmails: [],
      documentIds,
      createdAt: new Date().toISOString(),
      isRead: true,
    };

    if (replyToId && detail) {
      const replySource = detail.messages.find(m => m.id === replyToId);
      if (replySource) {
        optimisticMsg.replyToPreview = replySource.content.length > 100
          ? replySource.content.substring(0, 100) + '...'
          : replySource.content;
      }
    }

    queryClient.setQueryData<InquiryDetailDtoIn>([detailUrl], (old) => {
      if (!old) return old;
      return { ...old, messages: [...old.messages, optimisticMsg] };
    });

    sendMessageMutation.mutate({ content, isInternalNote, replyToMessageId: replyToId, attachmentDocumentIds: documentIds, mentionedUserEmails: [] });
  };

  const refreshDetail = () => {
    if (detailUrl) queryClient.invalidateQueries({ queryKey: [detailUrl] });
    queryClient.invalidateQueries({ queryKey: ['management/inquiries/query'] });
    queryClient.invalidateQueries({ queryKey: [`management/inquiries/stats?scope=${scope}`] });
  };

  const closeInquiryMutation = usePutAuthApi(selectedId ? `management/inquiries/${selectedId}/status` : '', { onSuccess: refreshDetail });
  const reopenInquiryMutation = usePutAuthApi(selectedId ? `management/inquiries/${selectedId}/status` : '', { onSuccess: refreshDetail });
  const assignMutation = usePutAuthApi(selectedId ? `management/inquiries/${selectedId}/assign` : '', { onSuccess: refreshDetail });

  const handleClose = () => {
    closeInquiryMutation.mutate({ params: {}, body: { status: 'Closed' } });
    setConfirmClose(false);
  };

  const handleReopen = () => {
    reopenInquiryMutation.mutate({ params: {}, body: { status: 'WaitingForOffice' } });
    setConfirmReopen(false);
  };

  /* מה שמחכה למשרד: חדשות + ממתינות למשרד. אותו מספר שהצליל והתג בכותרת מדווחים. */
  const waitingCount = (stats?.new ?? 0) + (stats?.waitingForOffice ?? 0);

  const getTabCount = (tab: string): number => {
    if (!stats) return 0;
    switch (tab) {
      case 'All': return stats.total;
      case 'New': return stats.new;
      case 'WaitingForOffice': return stats.waitingForOffice;
      case 'WaitingForUser': return stats.waitingForUser;
      case 'AnswerReceived': return stats.answerReceived;
      case 'Closed': return stats.closed;
      default: return 0;
    }
  };

  const formatDate = useCallback((dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + (dateStr.endsWith('Z') ? '' : 'Z'));
    const today = new Date();
    const israelDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' }));
    const israelToday = new Date(today.toLocaleString('en-US', { timeZone: 'Asia/Jerusalem' }));
    if (israelDate.toDateString() === israelToday.toDateString())
      return date.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem' });
    return date.toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' });
  }, []);

  const columns = useMemo<ColumnDef<ManagementInquiryDtoIn>[]>(() => [
    {
      id: 'id',
      header: t('inquiries.table.id'),
      accessorFn: (row) => row.id,
      cell: ({ getValue }) => <span className="body-sm-regular">#{getValue<number>()}</span>,
      meta: { className: 'w-[5%]' },
    },
    {
      id: 'subject',
      header: t('inquiries.table.subject'),
      accessorFn: (row) => row.subject,
      cell: ({ row: { original } }) => (
        <div className={styles.subjectCell}>
          <span className={`${styles.subjectText} body-sm-semibold`}>{original.subject}</span>
          {original.labels?.length > 0 && (
            <div className={styles.labelsRow}>
              {original.labels.map((label) => (
                <LabelChip key={label.id} name={label.name} color={label.color} />
              ))}
            </div>
          )}
        </div>
      ),
      meta: { className: 'w-[26%]' },
    },
    {
      id: 'createdBy',
      header: t('inquiries.table.coordinator'),
      accessorFn: (row) => row.createdByName,
      cell: ({ getValue }) => <span className="body-sm-regular">{getValue<string>()}</span>,
      meta: { className: 'w-[14%]' },
    },
    {
      id: 'fund',
      header: t('inquiries.table.fund'),
      accessorFn: (row) => row.fundName || row.fundId,
      cell: ({ getValue }) => <span className="body-sm-regular">{getValue<string | number>()}</span>,
      meta: { className: 'w-[8%]' },
    },
    {
      id: 'status',
      header: t('inquiries.table.status'),
      accessorFn: (row) => row.status,
      cell: ({ row: { original } }) => <InquiryStatusChip status={original.status} closedReason={original.closedReason} />,
      meta: { className: 'w-[14%]' },
    },
    {
      id: 'assignedTo',
      header: t('inquiries.table.agent'),
      accessorFn: (row) => row.assignedToName || row.assignedToDepartmentName || '',
      cell: ({ getValue }) => {
        const val = getValue<string>();
        return <span className="body-sm-regular" style={{ color: val ? 'var(--font-body-1)' : 'var(--font-disabled)' }}>{val || '-'}</span>;
      },
      meta: { className: 'w-[14%]' },
    },
    {
      id: 'lastMessage',
      header: t('inquiries.table.lastMessage'),
      accessorFn: (row) => row.lastMessageAt,
      cell: ({ getValue }) => <span className="body-xs-sm-regular" style={{ color: 'var(--font-body-2)' }}>{formatDate(getValue<string>())}</span>,
      meta: { className: 'w-[11%]' },
    },
  ], [t, formatDate]);

  const activeRecord = useMemo(() =>
    result?.data?.find(r => r.id === selectedId), [result, selectedId]);

  const bladeHeaderTop = detail && (
    <div className={styles.bladeTitle}>
      <span className="body-lr-semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {detail.subject}
      </span>
      <div className={styles.bladeTitleRow}>
        <InquiryStatusChip status={detail.status} closedReason={detail.closedReason} />
        <span className="body-xs-sm-regular">#{detail.id}</span>
        <span className="body-xs-sm-regular">{detail.createdByName}</span>
      </div>
      {detail.labels.length > 0 && (
        <div className={styles.bladeLabels}>
          {detail.labels.map((label) => (
            <LabelChip key={label.id} name={label.name} color={label.color} />
          ))}
        </div>
      )}
    </div>
  );

  /*
   * ⚠️ Rendered in the blade BODY, not in headerTop where it started. The shared Blade
   * header is a dark navy surface with white text, and a white-and-mint details card sitting
   * inside it looked like a pasted-in fragment - visible the moment a screenshot was taken.
   * Above the thread it reads as what it is: context for the conversation below it.
   */
  const bladeResource = detail && (
    <>
      {/*
      * The whole resource, not just a link to it. A secretary answering "בירור על תנועה
      * מ-12.08" had to open the transactions board in another tab to learn which
      * transaction; the fields travel with the inquiry now. The link stays for the cases the
      * snapshot cannot cover - checking whether it has changed since.
      */}
    <InquiryResourceSummary
      resourceType={detail.resourceType}
      resourceId={detail.resourceId}
      fields={detail.resourceSummary}
      dense
      onOpenResource={() => {
        const fId = detail.fundId;
        const routes: Record<string, string> = {
          Transaction: `/${fId}/transactions`,
          StandingOrder: `/${fId}/standing-orders`,
          SupportRequest: '/management/support-requests',
          Supported: '/management/supporteds',
          Goal: `/${fId}/support/goals`,
        };
        const route = routes[detail.resourceType!];
        if (route) window.open(`/new${route}?resourceId=${detail.resourceId}`, '_blank');
      }}
      />
    </>
  );

  const bladeHeaderBottom = detail && (
    <div className={styles.toolbar}>
      {detail.status === InquiryStatus.CLOSED ? (
        <Button variant="outline" onClick={() => setConfirmReopen(true)}>
          <LockOpenIcon sx={{ fontSize: 16, marginInlineEnd: '4px' }} />
          {t('inquiries.actions.reopen')}
        </Button>
      ) : (
        <Button variant="outline" onClick={() => setConfirmClose(true)}>
          <LockIcon sx={{ fontSize: 16, marginInlineEnd: '4px' }} />
          {t('inquiries.actions.close')}
        </Button>
      )}
      <Button variant="outline" onClick={(e) => setAssigneeAnchor(e.currentTarget)}>
        <PersonIcon sx={{ fontSize: 16, marginInlineEnd: '4px' }} />
        {detail.assignedToName && detail.assignedToDepartmentName
          ? `${detail.assignedToName} · ${detail.assignedToDepartmentName}`
          : detail.assignedToName || detail.assignedToDepartmentName || t('inquiries.actions.assign')}
      </Button>
      <Button variant="outline" onClick={(e) => setLabelAnchor(e.currentTarget)}>
        <LabelIcon sx={{ fontSize: 16, marginInlineEnd: '4px' }} />
        {detail.labels.length > 0
          ? `${t('inquiries.actions.labels')} (${detail.labels.length})`
          : t('inquiries.actions.labels')}
      </Button>
    </div>
  );

  return (
    <div className={styles.page}>
      {/*
        * One row for what this screen is, one for the tools, one for the status tabs. The
        * scope toggle used to sit on a line of its own between them, and the filters shared a
        * line with nothing - three rows that each carried one control and no grouping.
        */}
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <h2 className="heading-h3-semibold">{t('inquiries.management.title')}</h2>
          {/* The queue depth, in words, beside the title - the same number the chime rings on */}
          {waitingCount > 0 && (
            <span className={`body-sm-regular ${styles.waitingCount}`}>
              {t('inquiries.management.waitingCount', { count: waitingCount })}
            </span>
          )}
        </div>

        <div className={styles.headerActions}>
          {/* הגדרות ההתראות, שירדו מהכותרת העליונה. המשרד הוא המשתמש הכבד של הצליל,
              ובלעדי זה הן היו נשארות רק במסך של הרכז - כלומר לא נגישות למי שצריך אותן יותר */}
          <InquiryAlertSettingsButton />
          <div className={styles.scopeToggle}>
            {['All', 'Mine'].map((s) => (
              <button
                key={s}
                type="button"
                className={scope === s ? styles.scopeBtnActive : styles.scopeBtn}
                onClick={() => setScope(s)}
              >
                {t(`inquiries.scope.${s.toLowerCase()}`)}
              </button>
            ))}
          </div>

          {/*
            * The alert settings are NOT repeated here. They sit in the header indicator, which
            * is on every screen - a secretary who wants the chime off wants it off while she is
            * working elsewhere, which is the only time it matters.
            */}
          <button className={styles.settingsBtn} onClick={() => navigate('/management/settings/inquiries')} title={t('inquiries.management.settings')}>
            <SettingsIcon sx={{ fontSize: 22 }} />
          </button>
        </div>
      </div>

      <div className={styles.labelFilter}>
        <button
          type="button"
          className={filterLabelIds.length > 0 ? styles.labelFilterBtnActive : styles.labelFilterBtn}
          onClick={(e) => setLabelFilterAnchor(e.currentTarget)}
        >
          <FilterListIcon sx={{ fontSize: 16 }} />
          {filterLabelIds.length > 0
            ? `${t('inquiries.actions.labels')} (${filterLabelIds.length})`
            : t('inquiries.filter.byLabel')}
        </button>
        {filterLabelIds.length > 0 && (
          <>
            {filterLabelIds.map((id) => {
              const label = allLabels?.find(l => l.id === id);
              return label ? (
                <span key={id} className={styles.labelFilterChip}>
                  <LabelChip name={label.name} color={label.color} />
                  <button
                    type="button"
                    className={styles.labelFilterChipRemove}
                    onClick={() => setFilterLabelIds(prev => prev.filter(lid => lid !== id))}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </button>
                </span>
              ) : null;
            })}
            <button
              type="button"
              className={styles.labelFilterClear}
              onClick={() => setFilterLabelIds([])}
            >
              {t('inquiries.filter.clearLabels')}
            </button>
          </>
        )}
        <LabelPicker
          anchorEl={labelFilterAnchor}
          onClose={() => setLabelFilterAnchor(null)}
          selectedLabelIds={filterLabelIds}
          onToggleLabel={(labelId) => {
            setFilterLabelIds(prev =>
              prev.includes(labelId) ? prev.filter(id => id !== labelId) : [...prev, labelId]
            );
            setPage(1);
          }}
        />

        {showCreateLabel ? (
          <span className={styles.createLabelInline}>
            <input
              className={styles.createLabelInput}
              placeholder={t('inquiries.filter.newLabelName')}
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newLabelName.trim()) {
                  createLabelMutation.mutate({ name: newLabelName.trim(), color: '#3d608a' } as never);
                }
                if (e.key === 'Escape') { setShowCreateLabel(false); setNewLabelName(''); }
              }}
              autoFocus
              disabled={createLabelMutation.isPending}
            />
            <button
              type="button"
              className={styles.createLabelSubmit}
              onClick={() => { if (newLabelName.trim()) createLabelMutation.mutate({ name: newLabelName.trim(), color: '#3d608a' } as never); }}
              disabled={!newLabelName.trim() || createLabelMutation.isPending}
            >
              {t('inquiries.filter.create')}
            </button>
            <button
              type="button"
              className={styles.createLabelClose}
              onClick={() => { setShowCreateLabel(false); setNewLabelName(''); }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </button>
          </span>
        ) : (
          <button
            type="button"
            className={styles.labelFilterBtn}
            onClick={() => setShowCreateLabel(true)}
          >
            <AddIcon sx={{ fontSize: 14 }} />
            {t('inquiries.filter.createLabel')}
          </button>
        )}
      </div>

      <div className={styles.tabs}>
        {statusTabs.map((tab) => {
          const count = getTabCount(tab);
          return (
            <button
              key={tab}
              type="button"
              className={statusTab === tab ? styles.tabActive : styles.tab}
              onClick={() => { setStatusTab(tab); setPage(1); }}
            >
              {t(`inquiries.tabs.${tabKeyMap[tab]}`)}
              {count > 0 && <span className={styles.tabBadge}>{count}</span>}
            </button>
          );
        })}
      </div>

      <DataTable<ManagementInquiryDtoIn>
        columns={columns}
        data={result?.data ?? []}
        isLoading={isLoading}
        title={t('inquiries.management.listHeading')}
        searchQuery={search}
        setSearchQuery={setSearch}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="inquiriesManagement"
        searchPlaceholder="inquiries.filters.searchManagement"
        customGlobalSearch={() => true}
        handleRowClick={(row) => navigate(`/management/inquiries/${row.id}`)}
        activeRecord={activeRecord}
        emptyStateContent={{ title: t('inquiries.management.noData'), description: t('inquiries.management.noDataDescription') }}
      />

      {selectedId && detail && (
        <Blade
          handleXclick={() => navigate('/management/inquiries')}
          headerTop={bladeHeaderTop}
          headerBottom={bladeHeaderBottom}
          width="32%"
        >
          {bladeResource && <div className={styles.bladeResourceWrap}>{bladeResource}</div>}

          <div className={styles.chatWrapper}>
            <ChatPanel
              messages={detail.messages}
              onSendMessage={sendMessage}
              isManagement={true}
              isClosed={detail.status === InquiryStatus.CLOSED}
            />
          </div>

          <LabelPicker
            anchorEl={labelAnchor}
            onClose={() => setLabelAnchor(null)}
            selectedLabelIds={detail.labels.map(l => l.id)}
            inquiryId={detail.id}
            onChanged={refreshDetail}
          />
          <AssigneePicker
            anchorEl={assigneeAnchor}
            onClose={() => setAssigneeAnchor(null)}
            currentEmail={detail.assignedToEmail}
            currentDepartmentId={detail.assignedToDepartmentId}
            currentAgentName={detail.assignedToName}
            currentDepartmentName={detail.assignedToDepartmentName}
            onAssign={(email, deptId) => {
              assignMutation.mutate({ params: {}, body: { email, departmentId: deptId } });
              setAssigneeAnchor(null);
            }}
          />
        </Blade>
      )}

      <CustomDialog open={confirmClose} setOpen={setConfirmClose} onBtnClick={handleClose} btnText="inquiries.confirm.yes">
        <div className={styles.confirmContent}>
          <p className={styles.confirmText}>{t('inquiries.confirm.closeMessage')}</p>
        </div>
      </CustomDialog>

      <CustomDialog open={confirmReopen} setOpen={setConfirmReopen} onBtnClick={handleReopen} btnText="inquiries.confirm.yes">
        <div className={styles.confirmContent}>
          <p className={styles.confirmText}>{t('inquiries.confirm.reopenMessage')}</p>
        </div>
      </CustomDialog>
    </div>
  );
}
